import hello from "hellojs"
import axios, { AxiosRequestConfig } from "axios"
import { ServiceClientCredentials, HttpHeaders } from "@azure/ms-rest-js"

const keyOf = (network: string, scope?: string) => scope ? `${network}_${scope}` : network

export const login = (network?: string | undefined, options?: hello.HelloJSLoginOptions | undefined): PromiseLike<hello.HelloJSLoginEventArguement> => {
  return hello.login(network, options)
}

export const logout = (network?: string | undefined, options?: hello.HelloJSLogoutOptions | undefined): PromiseLike<any> => {
  return hello.logout(network, options)
}

export const getToken = (scope?: string | undefined, network?: string | undefined): PromiseLike<string | undefined> => {
  return (
    hello.login(keyOf(network || "b2c", scope), { force: false, scope })
      .then(undefined, _ => hello.login())
      .then(a => a.authResponse ? a.authResponse.access_token : undefined)
  )
}

export const getAuthResponse = (scope?: string | undefined, network?: string | undefined): Required<hello.HelloJSAuthResponse> | undefined => {
  let authResponse = hello.getAuthResponse(keyOf(network || "b2c", scope))
  return authResponse ? authResponse as Required<hello.HelloJSAuthResponse> : undefined
}

export function mkAxiosInstance(options: { scope?: string, network?: string, config?: AxiosRequestConfig } = {}) {
  const instance = axios.create(options.config)
  instance.interceptors.request.use(async config => {
    const token = await getToken(options.scope, options.network)
    if (token == undefined) {
      throw new Error("could not get token")
    }
    if (!config.headers) { config.headers = {} }
    config.headers["Authorization"] = `Bearer ${token}`
    return config
  })
  return instance
}

export function mkMSRestCreds(options: { scope?: string, network?: string }): ServiceClientCredentials {
  const creds: ServiceClientCredentials = {
    signRequest: async (webResource) => {
      const token = await getToken(options.scope, options.network)
      if (!webResource.headers) { webResource.headers = new HttpHeaders() }
      webResource.headers.set("Authorization", `Bearer ${token}`)
      return webResource
    }
  }
  return creds
}

export interface AzureClientConstructor<T, U> {
  new(credentials: ServiceClientCredentials, resourceId: string, options?: U): T
}

export interface AzureClientOptions<U> {
  scope?: string
  network?: string
  ctorArgs?: U
}

export function mkAzureClient<TClient, TOptions>(TCtor: AzureClientConstructor<TClient, TOptions>, resourceId: string, options: AzureClientOptions<TOptions> = {}) {
  const creds = mkMSRestCreds(options)
  return new TCtor(creds, resourceId, options.ctorArgs)
}

export default {
  login,
  logout,
  getToken,
  getAuthResponse,
  mkMSRestCreds,
  mkAzureClient,
  mkAxiosInstance
}

